import type { ReactNode } from 'react'
import { mensajeDeError } from '../api/mensajes'
import { IconoCerrar } from './iconos'

/**
 * Piezas comunes de los formularios de la spec 002 (clientes, catálogo,
 * usuarios, ajustes). Solo tokens del panel (`index.css`), sin hex sueltos.
 */
export const CLASE_INPUT =
  'w-full rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm text-tinta focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-foco'

export const CLASE_BOTON_PRIMARIO =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-acento px-4 py-2 text-sm font-semibold text-tinta-inversa transition-colors hover:bg-acento-fuerte disabled:cursor-not-allowed disabled:bg-desactivado disabled:text-desactivado-tinta'

export const CLASE_BOTON =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado disabled:cursor-not-allowed disabled:opacity-50'

export const CLASE_BOTON_PELIGRO =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-atencion-anillo bg-atencion-fondo px-3 py-2 text-sm font-semibold text-atencion-tinta transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50'

export function Campo({ etiqueta, children, ayuda }: { etiqueta: string; children: ReactNode; ayuda?: string }) {
  // La ayuda va fuera del <label>: así el nombre accesible del campo es solo
  // la etiqueta y no «Latitud Entre -90 y 90…».
  return (
    <div className="flex flex-col gap-1">
      <label className="flex flex-col gap-1 text-xs font-medium text-tinta-2">
        <span>{etiqueta}</span>
        {children}
      </label>
      {ayuda ? <span className="text-xs text-tinta-apagada">{ayuda}</span> : null}
    </div>
  )
}

/** Error de una acción (409, 422...) dicho con la frase del contrato. */
export function AvisoError({ error }: { error: unknown }) {
  if (!error) return null
  return (
    <p role="alert" className="rounded-lg border border-critico-anillo bg-critico-fondo px-3 py-2 text-sm text-critico-tinta">
      {mensajeDeError(error)}
    </p>
  )
}

export function CabeceraPagina({
  titulo,
  descripcion,
  acciones,
}: {
  titulo: string
  descripcion?: ReactNode
  acciones?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold text-tinta">{titulo}</h1>
        {descripcion ? <p className="mt-1 max-w-3xl text-sm text-tinta-apagada">{descripcion}</p> : null}
      </div>
      {acciones ? <div className="flex flex-wrap gap-2">{acciones}</div> : null}
    </div>
  )
}

/** Cabecera de un `Modal` con su botón de cerrar (mismo patrón que PanelDia). */
export function CabeceraModal({ titulo, onCerrar }: { titulo: string; onCerrar: () => void }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-borde px-5 py-4">
      <h2 className="text-base font-semibold text-tinta">{titulo}</h2>
      <button
        type="button"
        onClick={onCerrar}
        aria-label="Cerrar"
        className="grid size-8 place-items-center rounded-md text-tinta-2 ring-1 ring-borde hover:bg-lienzo focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco"
      >
        <IconoCerrar aria-hidden="true" className="size-4" />
      </button>
    </div>
  )
}

/** Insignia de estado: positivo (validado), neutro o atención. */
export function Insignia({ tono, children }: { tono: 'positivo' | 'neutro' | 'atencion' | 'critico'; children: ReactNode }) {
  const clases = {
    positivo: 'bg-positivo-fondo text-positivo-tinta',
    neutro: 'bg-elevado text-tinta-2',
    atencion: 'bg-atencion-fondo text-atencion-tinta',
    critico: 'bg-critico-fondo text-critico-tinta',
  }[tono]
  return <span className={`inline-flex shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${clases}`}>{children}</span>
}
